import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { fdateShort } from "@/lib/format";
import { PageHeader, Loader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LifeBuoy, Send, MessageSquare } from "lucide-react";

const CATS = [["technique", "Problème technique"], ["abonnement", "Abonnement / Premium"], ["paiement", "Paiement"],
  ["site", "Site de mariage"], ["gazette", "Gazette"], ["invites", "Invités / tables"], ["faire_part", "Faire-part"],
  ["prestataires", "Prestataires"], ["autre", "Autre"]];
const STATUS_LABEL = { nouveau: "Nouveau", en_cours: "En cours", repondu: "Répondu", ferme: "Fermé" };

export default function Support() {
  const location = useLocation();
  const [tickets, setTickets] = useState(null);
  const [form, setForm] = useState({ category: "technique", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState("");

  const load = async () => { const { data } = await api.get("/support/tickets"); setTickets(data); };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.subject.trim() || !form.message.trim()) { toast.error("Sujet et message requis"); return; }
    setSending(true);
    try {
      await api.post("/support/tickets", { ...form, page: location.pathname });
      toast.success("Votre demande a bien été envoyée");
      setForm({ category: "technique", subject: "", message: "" }); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSending(false); }
  };
  const sendReply = async (tid) => {
    if (!reply.trim()) return;
    try { await api.post(`/support/tickets/${tid}/reply`, { content: reply }); setReply(""); toast.success("Message envoyé"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (!tickets) return <Loader />;

  return (
    <div>
      <PageHeader eyebrow="Aide" title="Besoin d'aide ?"
        description="Une question, un souci ? Écrivez-nous, nous vous répondrons rapidement." />
      <div className="grid lg:grid-cols-[1fr_1fr] gap-6 items-start">
        <div className="card-lux p-6 space-y-4">
          <div className="flex items-center gap-2"><LifeBuoy className="w-5 h-5 text-champagne" /><h3 className="font-serif text-lg">Nouvelle demande</h3></div>
          <div><Label>Catégorie</Label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="support-category"
              className="mt-1 w-full h-10 rounded-md border px-3 text-sm bg-white">
              {CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
          <div><Label>Sujet</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1 h-10" data-testid="support-subject" /></div>
          <div><Label>Message</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={6} className="mt-1" data-testid="support-message" /></div>
          <Button onClick={submit} disabled={sending} className="rounded-full w-full" data-testid="support-submit">
            <Send className="w-4 h-4 mr-1" /> {sending ? "Envoi…" : "Envoyer ma demande"}</Button>
        </div>

        <div>
          <div className="eyebrow mb-3">Mes demandes</div>
          {tickets.length === 0 ? (
            <EmptyState icon={MessageSquare} title="Aucune demande" description="Vos échanges avec le support apparaîtront ici." />
          ) : (
            <div className="space-y-2">
              {tickets.map((t) => (
                <div key={t.id} className="card-lux p-4" data-testid={`ticket-${t.id}`}>
                  <button className="w-full text-left" onClick={() => setOpen(open === t.id ? null : t.id)}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-sm">{t.subject}</div>
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-accent text-stone-600">{STATUS_LABEL[t.status]}</span>
                    </div>
                    <div className="text-xs text-stone-400 mt-1">{fdateShort(t.created_at)} · {t.messages.length} message(s)</div>
                  </button>
                  {open === t.id && (
                    <div className="mt-3 space-y-2 border-t pt-3">
                      {t.messages.map((m) => (
                        <div key={m.id} className={`text-sm p-2.5 rounded-lg ${m.sender_type === "admin" ? "bg-sage/10" : "bg-accent/50"}`}>
                          <div className="text-[11px] text-stone-400 mb-0.5">{m.sender_type === "admin" ? "Support" : m.sender_name} · {fdateShort(m.created_at)}</div>
                          <div className="whitespace-pre-wrap text-stone-700">{m.content}</div>
                        </div>
                      ))}
                      <div className="flex gap-2 pt-1">
                        <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Répondre…" className="h-9" data-testid={`reply-input-${t.id}`} />
                        <Button size="sm" onClick={() => sendReply(t.id)} data-testid={`reply-send-${t.id}`}><Send className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
